import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper function to calculate total stock (original, for actual stock data)
const calculateTotalStock = (data) => {
  return data.reduce((sum, item) => sum + (item.stock || 0), 0);
};

// Helper function to calculate total stock price (original, for actual stock data)
const calculateTotalPriceOfStock = (data) => {
  return data.reduce((sum, item) => sum + ((item.stock || 0) * (item.price || 0)), 0);
};

// Helper function to calculate total raw material value
const calculateTotalRawMaterialValue = (data) => {
  // Parsing currency string back to number for calculation if needed, but here we expect pre-calculated values or raw numbers
  // However, looking at the plan and data, we will likely pass formatted strings or raw numbers.
  // Let's assume we pass raw numbers in a specific way or just sum the raw data before formatting if possible.
  // Actually, for the summary in `exportToExcel` and `exportToPdf`, we iterate over `data`.
  // If `data` contains formatted strings, we can't easily sum them.
  // Strategy: The component should calculate the totals and pass them, OR we calculate here if data allows.
  // But standard pattern here is calculating from `data` array which is ready for export (keys are Indonesian headers).
  // Let's look at `prepareRawMaterialStockDataForExport`. It maps to headers.
  // So 'Total Stok' in the row might be a number or string.
  // use `prepareRawMaterialStockDataForExport` sets 'Total Stok' as number.
  return data.reduce((sum, item) => sum + (item['Total Nilai'] || 0), 0);
};

const calculateTotalRawMaterialStock = (data) => {
  return data.reduce((sum, item) => sum + (item['Total Stok'] || 0), 0);
}

// New helper function to calculate total sold items from prepared data
const calculateTotalSoldItems = (data) => {
  return data.reduce((sum, item) => sum + (item['Terjual'] || 0), 0);
};

// New helper function to calculate total revenue from prepared sold items data
const calculateTotalRevenueFromSoldItems = (data) => {
  return data.reduce((sum, item) => sum + (item['Pendapatan'] || 0), 0);
};

// New helper function to calculate total amount (for expenses)
const calculateTotalAmount = (data) => {
  return data.reduce((sum, item) => sum + (item.Jumlah || 0), 0);
};

const formatCurrency = (value) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(value);

export const exportToExcel = (data, filename = 'data') => {
  // Check if it's combined data based on the presence of 'title', 'headers', and 'rows' keys
  const isCombinedData = data.length > 0 && data[0].hasOwnProperty('title') && data[0].hasOwnProperty('headers') && data[0].hasOwnProperty('rows');

  if (isCombinedData) {
    const ws = XLSX.utils.json_to_sheet([]);
    let rowIdx = 1;

    // Calculate max columns needed for merging
    let maxCols = 0;
    data.forEach(section => {
      if (section.headers && section.headers.length > maxCols) {
        maxCols = section.headers.length;
      }
    });
    // Ensure a minimum of 2 columns for summary sections if no headers are found or are too small
    if (maxCols < 2) maxCols = 2;

    // Define border style
    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    };

    // Main Report Title
    XLSX.utils.sheet_add_aoa(ws, [["Laporan Bisnis Gabungan"]], { origin: `A${rowIdx}` });
    if (ws[`A${rowIdx}`]) {
      ws[`A${rowIdx}`].s = {
        font: { bold: true, sz: 18, color: { rgb: 'FFFFFF' } },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: '4472C4' } },
        border: borderStyle
      };
    }
    // Merge main title
    if (maxCols > 1) {
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push({ s: { r: rowIdx - 1, c: 0 }, e: { r: rowIdx - 1, c: maxCols - 1 } });
      // Apply border to all merged cells in title
      for (let c = 0; c < maxCols; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx - 1, c });
        if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
        ws[cellRef].s = {
          font: { bold: true, sz: 18, color: { rgb: 'FFFFFF' } },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: '4472C4' } },
          border: borderStyle
        };
      }
    }
    rowIdx++;

    // Export Date
    XLSX.utils.sheet_add_aoa(ws, [[`Tanggal Export: ${new Date().toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' })}`]], { origin: `A${rowIdx}` });
    if (ws[`A${rowIdx}`]) {
      ws[`A${rowIdx}`].s = {
        font: { sz: 11 },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: 'E7E6E6' } },
        border: borderStyle
      };
    }
    // Merge date
    if (maxCols > 1) {
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push({ s: { r: rowIdx - 1, c: 0 }, e: { r: rowIdx - 1, c: maxCols - 1 } });
      // Apply border to all merged cells in date
      for (let c = 0; c < maxCols; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx - 1, c });
        if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
        ws[cellRef].s = {
          font: { sz: 11 },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: 'E7E6E6' } },
          border: borderStyle
        };
      }
    }
    rowIdx += 2; // Add spacing after main title and date

    data.forEach(section => {
      const sectionStartRow = rowIdx;

      // Section Title
      XLSX.utils.sheet_add_aoa(ws, [[section.title]], { origin: `A${rowIdx}` });
      if (ws[`A${rowIdx}`]) {
        ws[`A${rowIdx}`].s = {
          font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } },
          alignment: { horizontal: "left", vertical: "center" },
          fill: { fgColor: { rgb: '70AD47' } },
          border: borderStyle
        };
      }
      // Merge section title
      if (maxCols > 1) {
        if (!ws['!merges']) ws['!merges'] = [];
        ws['!merges'].push({ s: { r: rowIdx - 1, c: 0 }, e: { r: rowIdx - 1, c: maxCols - 1 } });
        // Apply border to all merged cells in section title
        for (let c = 0; c < maxCols; c++) {
          const cellRef = XLSX.utils.encode_cell({ r: rowIdx - 1, c });
          if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
          ws[cellRef].s = {
            font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } },
            alignment: { horizontal: "left", vertical: "center" },
            fill: { fgColor: { rgb: '70AD47' } },
            border: borderStyle
          };
        }
      }
      rowIdx++;

      // Section Headers
      XLSX.utils.sheet_add_aoa(ws, [section.headers], { origin: `A${rowIdx}` });
      section.headers.forEach((_, colIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx - 1, c: colIdx });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
            alignment: { horizontal: "center", vertical: "center" },
            fill: { fgColor: { rgb: '5B9BD5' } },
            border: borderStyle
          };
        }
      });
      rowIdx++;

      // Section Rows
      XLSX.utils.sheet_add_aoa(ws, section.rows, { origin: `A${rowIdx}` });
      // Apply styling to data rows
      section.rows.forEach((row, rowOffset) => {
        const isEvenRow = rowOffset % 2 === 0;
        row.forEach((_, colIdx) => {
          const cellRef = XLSX.utils.encode_cell({ r: rowIdx - 1 + rowOffset, c: colIdx });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              alignment: { horizontal: "left", vertical: "center" },
              fill: { fgColor: { rgb: isEvenRow ? 'FFFFFF' : 'F2F2F2' } },
              border: borderStyle
            };
          }
        });
      });
      rowIdx += section.rows.length;
      rowIdx++; // Add spacing between sections
    });

    // Auto-width columns
    const range = XLSX.utils.decode_range(ws['!ref']);
    ws['!cols'] = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let max_width = 10; // default width
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && cell.v) {
          max_width = Math.max(max_width, cell.v.toString().length + 2); // +2 for padding
        }
      }
      ws['!cols'][C] = { wch: max_width };
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Laporan Gabungan");
    XLSX.writeFile(wb, `${filename}.xlsx`);
    return;
  }

  // Original logic for single-type data (menu items, expenses, or sold items)
  const isExpenseData = filename.includes('Pengeluaran'); // Simple check
  const isSoldStockData = filename.includes('Stok_Terjual'); // Check for sold stock data
  const isTopSellingData = filename.includes('Menu_Terlaris'); // New check for top selling items data
  const isRawMaterialStockData = filename.includes('Stok_Bahan_Mentah'); // Check for raw material stock data

  let title;
  let summaryRows = [];

  if (isExpenseData) {
    title = "Laporan Pengeluaran";
    const totalAmount = calculateTotalAmount(data);
    summaryRows = [
      [], // Empty row before summary
      ['Total Pengeluaran:', totalAmount.toLocaleString('id-ID')]
    ];
  } else if (isSoldStockData) {
    title = "Laporan Stok Menu Terjual";
    const totalSoldItems = calculateTotalSoldItems(data);
    const totalRevenueFromSoldItems = calculateTotalRevenueFromSoldItems(data);
    summaryRows = [
      [], // Empty row before summary
      ['Total Item Terjual:', totalSoldItems],
      ['Total Pendapatan:', formatCurrency(totalRevenueFromSoldItems)]
    ];
  } else if (isTopSellingData) { // Handle top selling items data summary
    title = "Laporan Menu Terlaris";
    const totalSoldItems = data.reduce((sum, item) => sum + (item['Terjual'] || 0), 0);
    const totalRevenueFromSoldItems = data.reduce((sum, item) => sum + (item['Pendapatan'] || 0), 0);
    summaryRows = [
      [], // Empty row before summary
      ['Total Item Terjual:', totalSoldItems.toString()],
      ['Total Pendapatan:', formatCurrency(totalRevenueFromSoldItems)]
    ];
  } else if (isRawMaterialStockData) {
    title = "Laporan Stok Bahan Mentah";
    const totalStock = calculateTotalRawMaterialStock(data);
    const totalValue = calculateTotalRawMaterialValue(data);
    summaryRows = [
      [], // Empty row before summary
      ['Total Quantity Stok:', totalStock],
      ['Total Nilai Stok:', formatCurrency(totalValue)]
    ];
  } else if (filename.includes('Rekonsiliasi_Kas')) {
    title = "Laporan Rekonsiliasi Kas";
    const totalActual = data.reduce((sum, item) => sum + (item['Total Aktual'] || 0), 0);
    const totalVariance = data.reduce((sum, item) => sum + (item['Total Selisih'] || 0), 0);
    summaryRows = [
      [],
      ['Total Seluruh Aktual:', formatCurrency(totalActual)],
      ['Total Seluruh Selisih:', formatCurrency(totalVariance)]
    ];
  } else {
    title = "Laporan Stok Menu"; // Default title for original stock data
    const totalStock = calculateTotalStock(data);
    const totalPriceOfStock = calculateTotalPriceOfStock(data);
    summaryRows = [
      [], // Empty row before summary
      ['Total Stok:', totalStock],
      ['Total Harga Stok:', formatCurrency(totalPriceOfStock)]
    ];
  }


  // Prepare data for Excel
  const ws_data = [
    [title],
    [],
    Object.keys(data[0] || {}),
    ...data.map(item => Object.values(item)),
    ...summaryRows
  ];

  const ws = XLSX.utils.aoa_to_sheet(ws_data);

  // Merge title cells
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: Object.keys(data[0] || {}).length - 1 } }];

  // Define border style
  const borderStyle = {
    top: { style: 'thin', color: { rgb: '000000' } },
    bottom: { style: 'thin', color: { rgb: '000000' } },
    left: { style: 'thin', color: { rgb: '000000' } },
    right: { style: 'thin', color: { rgb: '000000' } }
  };

  // Get range of worksheet
  const range = XLSX.utils.decode_range(ws['!ref']);

  // Apply styling to all cells
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) continue;

      // Initialize cell style if not exists
      if (!ws[cellAddress].s) ws[cellAddress].s = {};

      // Add borders to all cells
      ws[cellAddress].s.border = borderStyle;

      // Title row (row 0)
      if (R === 0) {
        ws[cellAddress].s = {
          font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          fill: { fgColor: { rgb: '4472C4' } },
          border: borderStyle
        };
      }

      // Header row (row 2)
      if (R === 2) {
        ws[cellAddress].s = {
          font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          fill: { fgColor: { rgb: '5B9BD5' } },
          border: borderStyle
        };
      }

      // Data rows - add alternating row colors
      if (R > 2 && R < range.e.r - summaryRows.length + 1) {
        const isEvenRow = (R - 3) % 2 === 0;
        ws[cellAddress].s = {
          alignment: { horizontal: 'left', vertical: 'center' },
          fill: { fgColor: { rgb: isEvenRow ? 'FFFFFF' : 'F2F2F2' } },
          border: borderStyle
        };
      }

      // Summary rows
      if (summaryRows.length > 0 && R >= range.e.r - summaryRows.length + 1) {
        ws[cellAddress].s = {
          font: { bold: true, sz: 11 },
          alignment: { horizontal: 'left', vertical: 'center' },
          fill: { fgColor: { rgb: 'E7E6E6' } },
          border: borderStyle
        };
      }
    }
  }

  // Auto-width columns
  const headerRowIndex = 2;
  const max_width = ws_data[headerRowIndex].map((header, i) => Math.max(...ws_data.map(row => (row[i] ? row[i].toString().length : 0)), header.length));
  ws['!cols'] = max_width.map(w => ({ wch: w + 2 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, isExpenseData ? "Pengeluaran" : (isSoldStockData ? "Stok Terjual" : (isTopSellingData ? "Menu Terlaris" : (isRawMaterialStockData ? "Stok Bahan Mentah" : "Stok Menu"))));
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportToPdf = (data, filename = 'data', dateRange = null) => {
  const doc = new jsPDF();
  const isCombinedData = data.length > 0 && data[0].hasOwnProperty('title') && data[0].hasOwnProperty('headers') && data[0].hasOwnProperty('rows');

  if (isCombinedData) {
    // Common PDF header
    doc.setFontSize(18);
    doc.text("Laporan Bisnis Gabungan", 14, 22);

    doc.setFontSize(11);
    doc.setTextColor(100);

    let subtitleY = 30;
    if (dateRange) {
      const formatDate = (date) => {
        if (!date) return '-';
        if (typeof date === 'string') {
          // check if it is yyyy-mm-dd
          if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [y, m, d] = date.split('-');
            const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
            return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
          }
          return date;
        }
        return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      };
      const start = dateRange.startDate || dateRange.start || dateRange;
      const end = dateRange.endDate || dateRange.end || dateRange;
      doc.text(`Periode: ${formatDate(start)} s/d ${formatDate(end)}`, 14, subtitleY);
      subtitleY += 7;
    }

    doc.text(`Tanggal Export: ${new Date().toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' })}`, 14, subtitleY);

    let finalY = 40;
    data.forEach(section => {
      // Calculate required space for title (~15mm) + at least one table row (~7mm) + margin (~20mm)
      const requiredSpace = 15 + 7 + 20;
      const remainingPageSpace = doc.internal.pageSize.height - finalY;

      // If not enough space for title and first row, add a new page
      // Also, ensure it's not the very first section on the very first page
      if (finalY > 40 && remainingPageSpace < requiredSpace) {
        doc.addPage();
        finalY = 20;
      }

      // Add section title
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text(section.title, 14, finalY + 10);
      finalY += 15;

      autoTable(doc, {
        startY: finalY,
        head: [section.headers],
        body: section.rows,
        theme: 'striped',
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [20, 83, 45], // Dark green
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240],
        },
        margin: { bottom: 20 },
        didDrawPage: function (data) {
          // Footer
          let str = "Halaman " + doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 10);
        }
      });
      finalY = doc.lastAutoTable.finalY;
      finalY += 10;
    });

    doc.save(`${filename}.pdf`);
    return;
  }

  const isExpenseData = filename.includes('Pengeluaran'); // Simple check
  const isTopSellingData = filename.includes('Menu_Terlaris'); // New check for top selling items data
  const isRawMaterialStockData = filename.includes('Stok_Bahan_Mentah'); // Check for raw material stock data

  let title = "Laporan Stok Menu";
  let summaryLines = [];

  if (isExpenseData) {
    title = "Laporan Pengeluaran";
    const totalAmount = calculateTotalAmount(data);
    summaryLines = [
      `Total Pengeluaran: ${formatCurrency(totalAmount)}`
    ];
  } else if (isTopSellingData) {
    title = "Laporan Menu Terlaris";
    const totalSoldItems = data.reduce((sum, item) => sum + (item['Terjual'] || 0), 0);
    const totalRevenueFromSoldItems = data.reduce((sum, item) => sum + (item['Pendapatan'] || 0), 0);
    summaryLines = [
      `Total Item Terjual: ${totalSoldItems}`,
      `Total Pendapatan: ${formatCurrency(totalRevenueFromSoldItems)}`
    ];
  } else if (isRawMaterialStockData) {
    title = "Laporan Stok Bahan Mentah";
    const totalStock = calculateTotalRawMaterialStock(data);
    const totalValue = calculateTotalRawMaterialValue(data);
    summaryLines = [
      `Total Quantity Stok: ${totalStock}`,
      `Total Nilai Stok: ${formatCurrency(totalValue)}`
    ];
  } else if (filename.includes('Rekonsiliasi_Kas')) {
    title = "Laporan Rekonsiliasi Kas";
    const totalActual = data.reduce((sum, item) => sum + (item['Total Aktual'] || 0), 0);
    const totalVariance = data.reduce((sum, item) => sum + (item['Total Selisih'] || 0), 0);
    summaryLines = [
      `Total Seluruh Aktual: ${formatCurrency(totalActual)}`,
      `Total Seluruh Selisih: ${formatCurrency(totalVariance)}`
    ];
  } else {
    const totalStock = calculateTotalStock(data);
    const totalPriceOfStock = calculateTotalPriceOfStock(data);
    summaryLines = [
      `Total Stok: ${totalStock}`,
      `Total Harga Stok: ${formatCurrency(totalPriceOfStock)}`
    ];
  }

  // Add title
  let finalY = 30; // Give more space from top
  doc.setFontSize(18);
  doc.text(title, 14, finalY);
  finalY += 10;

  doc.setFontSize(11);
  doc.setTextColor(100);

  if (dateRange) {
    const formatDate = (date) => {
      if (!date) return '-';
      if (typeof date === 'string') {
        // check if it is yyyy-mm-dd
        if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [y, m, d] = date.split('-');
          const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
          return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
        }
        return date;
      }
      return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    };
    const start = dateRange.startDate || dateRange.start || dateRange;
    const end = dateRange.endDate || dateRange.end || dateRange;
    doc.text(`Periode: ${formatDate(start)} s/d ${formatDate(end)}`, 14, finalY);
    finalY += 7;
  }

  doc.text(`Tanggal Export: ${new Date().toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' })}`, 14, finalY);
  finalY += 10;


  // Prepare table data
  const headers = Object.keys(data[0] || {});
  const body = data.map(item => Object.values(item));

  autoTable(doc, {
    startY: finalY, // Start table after title and date
    head: [headers],
    body: body,
    theme: 'striped',
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [20, 83, 45], // Dark green
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240],
    },
    margin: { bottom: 20 }, // Add bottom margin to prevent overlap with footer
    didDrawPage: function (data) {
      // Footer
      let str = "Halaman " + doc.internal.getNumberOfPages();
      doc.setFontSize(10);
      doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 10);
    }
  });

  // Add dynamic summary lines
  finalY = doc.lastAutoTable.finalY;
  doc.setFontSize(12);
  doc.setTextColor(0);
  summaryLines.forEach((line, index) => {
    doc.text(line, 14, finalY + 10 + (index * 7)); // 7 is approximate line height
  });

  doc.save(`${filename}.pdf`);
};

export const prepareTransactionDataForExport = (transactions) => {
  return transactions.map(transaction => ({
    'ID Transaksi': transaction.id,
    'Tanggal': new Date(transaction.timestamp).toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' }),
    'Waktu': new Date(transaction.timestamp).toLocaleTimeString('id-ID', { timeZone: 'Asia/Jakarta' }),
    'Total': `Rp ${parseFloat(transaction.total || 0).toLocaleString('id-ID')}`,
    'Tipe': transaction.isEmployeeMeal ? 'Karyawan' : transaction.payment_method,
    'Item Terjual': transaction.items.map(item => `${item.name} (${item.quantity})`).join(', '),
    'Kasir': transaction.cashier_name || 'System',
  }));
};

export const prepareExpenseDataForExport = (expenses) => {
  return expenses.map(expense => ({
    'ID Pengeluaran': expense.id,
    'Tanggal': new Date(expense.date).toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' }),
    'Waktu': new Date(expense.date).toLocaleTimeString('id-ID', { timeZone: 'Asia/Jakarta' }),
    'Kategori': expense.category,
    'Deskripsi': expense.name,
    'Jumlah': parseFloat(expense.amount || 0), // Keep as number for Excel sum
    'Catatan': expense.notes || '',
    'Dicatat Oleh': expense.recorded_by || 'System',
  }));
};

export const prepareStockDataForExport = (menuItems) => {
  return menuItems.map(item => ({
    'ID Menu': item.id,
    'Nama Menu': item.name,
    'Kategori': item.category,
    'Harga': parseFloat(item.price || 0),
    'Stok': parseInt(item.stock || 0, 10),
  }));
};

export const prepareTopSellingItemsForExport = (topItems) => {
  return topItems.map(item => ({
    'Nama Menu': item.name,
    'Terjual': item.sold,
    'Pendapatan': parseFloat(item.revenue || 0),
  }));
};

export const prepareSoldStockDataForExport = (soldItems) => {
  return soldItems.map(item => ({
    'Nama Menu': item.name,
    'Terjual': item.sold,
    'Pendapatan': parseFloat(item.revenue || 0),
  }));
};

export const prepareCombinedDataForExport = (reportData, units = []) => {

  const { sales = {}, expenses = {}, stock = {}, rawMetrics, balances = {}, allTime = {}, precision = {} } = reportData;
  const sections = [];

  // --- audit & financial summary section based on period ---
  // Use precision metrics from backend if available for accuracy
  const initialCap = precision.openingBalance !== undefined ? precision.openingBalance : parseFloat(sales.initialCapital || 0);
  const totalRevPeriod = precision.periodRevenue !== undefined ? precision.periodRevenue : parseFloat(sales.totalRevenue || 0);
  const totalExpPeriod = precision.periodExpenses !== undefined ? precision.periodExpenses : parseFloat(expenses.summary?.totalExpenses || 0);
  const netProfitPeriod = totalRevPeriod - totalExpPeriod;

  const ownerBal = parseFloat(balances.ownerBalance || 0);
  const lapakBal = parseFloat(balances.lapakBalance || 0);
  const currentCash = precision.physicalCashAtEnd !== undefined ? precision.physicalCashAtEnd : (ownerBal + lapakBal);

  const expectedCash = precision.expectedClosingBalance !== undefined ? precision.expectedClosingBalance : (initialCap + totalRevPeriod - totalExpPeriod);
  const auditDiff = currentCash - expectedCash;
  const stockVal = rawMetrics?.totalStockValue || 0;

  // Wealth = Modal Awal + Profit + Stock (What the business actually has)
  const workingModal = currentCash + stockVal;
  // Equity = Modal Awal + Laba Bersih (The target value)
  const equity = expectedCash + stockVal;

  sections.push({
    title: 'Ringkasan Audit & Keuangan (Periode Terpilih)',
    headers: ['Keterangan', 'Nilai'],
    rows: [
      ['Modal Awal Bisnis (Master)', formatCurrency(parseFloat(sales.initialCapital || 0))],
      ['Modal Awal Periode (Baseline)', formatCurrency(initialCap)],
      ['Total Pendapatan', formatCurrency(totalRevPeriod)],
      ['Total Pengeluaran', formatCurrency(totalExpPeriod)],
      ['Laba Bersih', formatCurrency(netProfitPeriod)],
      ['Nilai Stok Barang', formatCurrency(stockVal)],
      ['Kas Aktual', formatCurrency(currentCash)],
      ['Hasil Audit Kas', `${formatCurrency(auditDiff)} ${auditDiff < 0 ? '(Kurang)' : auditDiff > 0 ? '(Lebih)' : '(Pas ✓)'}`],
      ['Kekayaan Bisnis (Equity)', formatCurrency(expectedCash)],
      ['Modal Berjalan (Wealth)', formatCurrency(expectedCash + stockVal)],
    ],
  });

  // --- Combined Analytics Section (Current Period) ---
  const capitalGrowth = initialCap > 0 ? (netProfitPeriod / initialCap) * 100 : 0;

  sections.push({
    title: 'Analisis Gabungan (Periode Terpilih)',
    headers: ['Keterangan', 'Nilai'],
    rows: [
      ['Pendapatan Periode', formatCurrency(totalRevPeriod)],
      ['Pengeluaran Periode', formatCurrency(totalExpPeriod)],
      ['Laba Bersih Periode', formatCurrency(netProfitPeriod)],
      ['Pertumbuhan Modal (dari Laba Periode)', `${capitalGrowth.toFixed(1)}%`]
    ],
  });

  // Sales Summary
  sections.push({
    title: 'Ringkasan Penjualan',
    headers: ['Keterangan', 'Nilai'],
    rows: [
      ['Total Pendapatan', formatCurrency(sales.totalRevenue)],
      ['Total Transaksi', sales.totalTransactions.toString()],
      ['Rata-rata Order', formatCurrency(sales.averageOrder)],
    ],
  });

  // Top Selling Items
  sections.push({
    title: 'Menu Terlaris',
    headers: ['Nama Menu', 'Terjual', 'Pendapatan'],
    rows: sales.topItems.map(item => [item.name, item.sold.toString(), formatCurrency(item.revenue)]),
  });

  // Expense Summary
  sections.push({
    title: 'Ringkasan Pengeluaran',
    headers: ['Keterangan', 'Nilai'],
    rows: [
      ['Total Pengeluaran', formatCurrency(expenses.summary.totalExpenses)],
      ['Kategori Aktif', expenses.summary.categories.toString()],
      ['Rata-rata Harian', formatCurrency(expenses.summary.averageDaily)],
    ],
  });

  // Expense Details
  sections.push({
    title: 'Detail Pengeluaran',
    headers: ['Tanggal', 'Deskripsi', 'Kategori', 'Jumlah'],
    rows: expenses.items.map(item => [
      new Date(item.date).toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' }),
      item.name,
      item.category,
      formatCurrency(item.amount),
    ]),
  });

  // Stock Summary
  sections.push({
    title: 'Ringkasan Stok',
    headers: ['Keterangan', 'Nilai'],
    rows: [
      ['Total Nilai Stok', formatCurrency(stock.totalValue)],
      ['Jumlah Item', stock.totalItems.toString()],
      ['Stok Menipis', stock.lowStockItems.toString()],
    ],
  });

  // Low Stock Items
  sections.push({
    title: 'Item Stok Menipis',
    headers: ['Nama Menu', 'Stok', 'Harga'],
    rows: stock.lowStock.map(item => [item.name, item.stock.toString(), formatCurrency(item.price)]),
  });

  // --- NEW: Raw Materials Summary ---
  if (rawMetrics) {
    sections.push({
      title: 'Ringkasan Bahan Mentah',
      headers: ['Keterangan', 'Nilai'],
      rows: [
        ['Total Nilai Stok Menu', formatCurrency(rawMetrics.totalMenuStockValue)],
        ['Total Nilai Stok Mentah', formatCurrency(rawMetrics.currentRawMaterialStockValue)],
        ['Total Nilai Stok Gabungan', formatCurrency(rawMetrics.totalStockValue)],
        ['Bahan Stok Menipis', `${rawMetrics.lowStockRawCount} Jenis`],
      ],
    });

    if (rawMetrics.rawMaterials && rawMetrics.rawMaterials.length > 0) {
      const preparedRawMaterials = prepareRawMaterialStockDataForExport(rawMetrics.rawMaterials, units);
      sections.push({
        title: 'Detail Stok Bahan Mentah',
        headers: ['Nama Bahan', 'Stok Mentah', 'Stok Menu', 'Total Stok', 'Satuan', 'Total Nilai', 'Status'],
        rows: preparedRawMaterials.map(m => [
          m['Nama Bahan'],
          m['Stok Mentah'].toString(),
          m['Stok Menu'].toString(),
          m['Total Stok'].toString(),
          m['Satuan'],
          formatCurrency(m['Total Nilai']),
          m['Status']
        ]),
      });
    }
  }

  // --- NEW: Reconciliation Summary ---
  if (reportData.reconciliations && reportData.reconciliations.length > 0) {
    sections.push({
      title: 'Riwayat Rekonsiliasi Kas',
      headers: ['Tanggal', 'User', 'Tunai (Act)', 'Transfer (Act)', 'Gojek (Act)', 'Total Selisih', 'Status'],
      rows: reportData.reconciliations.map(item => {
        const totalVariance = (parseFloat(item.variance_tunai) || 0) + (parseFloat(item.variance_transfer) || 0) + (parseFloat(item.variance_gojek) || 0);
        let status = 'PAS';
        if (totalVariance > 0) status = 'KURANG';
        else if (totalVariance < 0) status = 'LEBIH';

        return [
          new Date(item.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }),
          item.reconciled_by || '-',
          formatCurrency(item.actual_tunai || 0),
          formatCurrency(item.actual_transfer || 0),
          formatCurrency(item.actual_gojek || 0),
          formatCurrency(Math.abs(totalVariance)),
          status
        ];
      }),
    });
  }

  return sections;
};

export const prepareRawMaterialStockDataForExport = (materials, units = []) => {
  return materials.map(material => {
    const currentStock = Number(material.current_stock) || 0;
    const menuStock = Number(material.menu_stock) || 0;
    const pricePerItem = Number(material.price_per_item) || 0;
    const rawStockPrice = Number(material.raw_stock_price) || 0;
    const totalStock = currentStock + menuStock;

    // Determine if unit is calculable
    const unitData = units.find(u => u.name.toLowerCase() === (material.unit || '').toLowerCase());
    const isCalculable = unitData ? unitData.is_calculable : true; // Default to true if not found

    let menuValue, rawValue, totalValue;
    if (isCalculable) {
      menuValue = menuStock * pricePerItem;
      rawValue = currentStock * rawStockPrice;
      totalValue = menuValue + rawValue;
    } else {
      // For non-calculable units, use prices directly
      menuValue = pricePerItem;
      rawValue = rawStockPrice;
      totalValue = pricePerItem + rawStockPrice;
    }

    return {
      'Nama Bahan': material.name,
      'Stok Mentah': currentStock,
      'Stok Menu': menuStock,
      'Total Stok': totalStock,
      'Satuan': material.unit,
      'Stok Min': Number(material.stok_minimum) || 0,
      'Harga Stok Menu': isCalculable ? pricePerItem : `${pricePerItem} (Nilai Langsung)`,
      'Harga Stok Mentah': isCalculable ? rawStockPrice : `${rawStockPrice} (Nilai Langsung)`,
      'Total Nilai': totalValue, // Keep as number for calculation
      'Status': totalStock < (Number(material.stok_minimum) || 0) ? 'Menipis' : 'Cukup'
    };
  });
};

export const prepareCashReconciliationDataForExport = (history) => {
  return history.map(item => {
    const totalVariance = parseFloat(item.variance_tunai || 0) + parseFloat(item.variance_transfer || 0) + parseFloat(item.variance_gojek || 0);
    const totalActual = parseFloat(item.actual_tunai || 0) + parseFloat(item.actual_transfer || 0) + parseFloat(item.actual_gojek || 0);

    let statusLabel = 'PAS';
    if (totalVariance > 0) statusLabel = 'KURANG';
    else if (totalVariance < 0) statusLabel = 'LEBIH';

    return {
      'Tanggal': new Date(item.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }),
      'User': item.reconciled_by || '-',
      'Tunai (Act)': parseFloat(item.actual_tunai || 0),
      'Transfer (Act)': parseFloat(item.actual_transfer || 0),
      'Gojek (Act)': parseFloat(item.actual_gojek || 0),
      'Total Aktual': totalActual,
      'Total Selisih': Math.abs(totalVariance),
      'Status': statusLabel,
      'Catatan': item.notes || '-'
    };
  });
};

export const prepareAuditDataForExport = (auditData) => {
  const { metrics, anomalies } = auditData;
  const sections = [];

  // Ringkasan Utama
  sections.push({
    title: 'Ringkasan Utama Audit',
    headers: ['Keterangan', 'Nilai'],
    rows: [
      ['Status Integritas', Math.abs(metrics.cashDifference) <= 100 ? 'Sangat Akurat (PAS)' : `Selisih Rp ${new Intl.NumberFormat('id-ID').format(Math.abs(metrics.cashDifference))}`],
      ['Modal Awal Bisnis (Master)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.masterInitialCapital)],
      ['Modal Awal Periode (Baseline)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.periodOpeningBalance)],
      ['Total Pendapatan', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.totalRevenue)],
      ['Total Pengeluaran', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.totalExpenses)],
      ['Laba Bersih', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.netProfit)],
      ['Nilai Stok Barang', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.stockValue)],
      ['Kas Aktual (Fisik)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.physicalCash)],
      ['Kas Seharusnya (Sistem)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.expectedCash)],
      ['Selisih Audit Kas', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.cashDifference)],
      ['Kekayaan Bisnis (Equity)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.targetEquity)],
      ['Modal Berjalan (Wealth)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.currentEquity)],
    ]
  });

  // Rincian Saldo
  sections.push({
    title: 'Rincian Saldo Aktual',
    headers: ['Sumber Kas', 'Nilai'],
    rows: [
      ['Saldo Transfer (Bank/Owner)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.ownerBalance)],
      ['Saldo Tunai (Lapak/Kasir)', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.lapakBalance)],
      ['Total Kas Aktual', new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(metrics.physicalCash)],
    ]
  });

  // Anomali Temuan
  if (anomalies && anomalies.length > 0) {
    sections.push({
      title: 'Temuan Anomali & Peringatan',
      headers: ['Judul', 'Deskripsi', 'Tingkat'],
      rows: anomalies.map(a => [a.title, a.desc, a.type === 'danger' ? 'KRITIKAL' : 'PERINGATAN'])
    });
  } else {
    sections.push({
      title: 'Temuan Anomali & Peringatan',
      headers: ['Status'],
      rows: [['Tidak ditemukan anomali data (Sempurna)']]
    });
  }

  return sections;
};

export const exportAuditorToPdf = (auditData, filename = 'audit_report', dateRange = null, template = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const { metrics, anomalies } = auditData;
  const currencyFormat = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 });

  // Gabungkan template dari pengaturan dengan default
  const DEFAULTS = {
    company_name: 'KASIR BERKAH 88',
    company_subtitle: 'Sistem Manajemen Keuangan & Point of Sale Terintegrasi',
    company_tagline: 'Laporan Resmi Transparansi Operasional & Integritas Data',
    report_title: 'LAPORAN AUDIT & INTEGRITAS KEUANGAN',
    recipient_label: 'Pimpinan / Owner Kasir Berkah 88',
    opening_salutation: 'Dengan hormat,',
    opening_body: 'Berdasarkan hasil analisis sistem terhadap seluruh data transaksi, pengeluaran, dan pergerakan kas pada periode yang ditentukan, berikut adalah rincian konsolidasi keuangan:',
    closing_body: 'Demikian laporan audit ini disampaikan sebagai rujukan utama dalam pengambilan keputusan manajemen dan audit operasional berkala.',
    issuer_label: 'Diterbitkan Secara Otomatis oleh,',
    issuer_name: 'Sistem Audit Kasir Berkah 88',
    signer_title: 'Admin & Tim Keuangan',
    signature_note: '(Dicetak melalui modul Auditor - Digital Signature Verified)',
  };
  const T = { ...DEFAULTS, ...template };

  // --- Letterhead (Kop Surat) ---
  doc.setFontSize(22);
  doc.setTextColor(20, 83, 45); // Dark Green
  doc.setFont('helvetica', 'bold');
  doc.text(T.company_name, pageWidth / 2, 20, { align: "center" });

  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.setFont('helvetica', 'normal');
  doc.text(T.company_subtitle, pageWidth / 2, 26, { align: "center" });
  doc.text(T.company_tagline, pageWidth / 2, 31, { align: "center" });

  // Line under letterhead
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(14, 35, pageWidth - 14, 35);
  doc.setLineWidth(1.5);
  doc.line(14, 36.5, pageWidth - 14, 36.5);

  // --- Document Title & Reference ---
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.text(T.report_title, pageWidth / 2, 48, { align: "center" });

  const reportId = `AUD-${new Date().getTime().toString().slice(-8)}`;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text(`Kode Referensi Laporan: ${reportId}`, pageWidth / 2, 53, { align: "center" });

  // --- Header Information ---
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.text("Kepada Yth,", 14, 65);
  doc.setFont('helvetica', 'bold');
  doc.text(T.recipient_label, 14, 70);

  doc.setFont('helvetica', 'normal');
  const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  doc.text(`Tanggal Cetak: ${today}`, pageWidth - 14, 70, { align: 'right' });

  // Date Range Info
  let currentY = 80;
  if (dateRange) {
    const formatDate = (date) => {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    };
    doc.setFont('helvetica', 'bold');
    doc.text(`Periode Laporan: ${formatDate(dateRange.startDate || dateRange.start)} s/d ${formatDate(dateRange.endDate || dateRange.end)}`, 14, currentY);
    currentY += 10;
  }

  doc.setFont('helvetica', 'normal');
  doc.text(T.opening_salutation, 14, currentY);
  currentY += 6;
  doc.text(T.opening_body, 14, currentY, { maxWidth: pageWidth - 28 });
  currentY += 12;

  // --- Content Tabular Data ---
  autoTable(doc, {
    startY: currentY,
    head: [['KOMPONEN AUDIT KEUANGAN', 'NILAI LAPORAN']],
    body: [
      ['Modal Awal (Baseline Audit)', currencyFormat.format(metrics.periodOpeningBalance)],
      ['Total Pemasukan (Revenue)', currencyFormat.format(metrics.totalRevenue)],
      ['Total Pengeluaran (Expense)', currencyFormat.format(metrics.totalExpenses)],
      ['Laba Bersih Operasional', currencyFormat.format(metrics.netProfit)],
      ['Nilai Persediaan Aset/Stok', currencyFormat.format(metrics.stockValue)],
      ['Estimasi Kas dari Sistem', currencyFormat.format(metrics.expectedCash)],
      ['Kas Aktual yang Tersedia', currencyFormat.format(metrics.physicalCash)],
      ['SELISIH AUDIT (ACCURACY)', currencyFormat.format(metrics.cashDifference)],
    ],
    theme: 'grid',
    headStyles: { fillColor: [31, 41, 55], textColor: 255, fontStyle: 'bold', fontSize: 10 },
    styles: { fontSize: 9, cellPadding: 4 },
    columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
    margin: { left: 14, right: 14 }
  });

  currentY = doc.lastAutoTable.finalY + 10;

  // --- Assessment / Conclusion ---
  doc.setFont('helvetica', 'bold');
  doc.text("KESIMPULAN VALIDASI DATA:", 14, currentY);
  currentY += 6;
  doc.setFont('helvetica', 'normal');

  let assessment = "";
  if (Math.abs(metrics.cashDifference) <= 100) {
    assessment = "Sistem validasi menyatakan data keuangan DALAM KONDISI SEMPURNA. Catatan digital sepenuhnya sinkron dengan uang fisik yang tersedia. Tidak ditemukan penyimpangan data.";
  } else if (metrics.cashDifference < 0) {
    assessment = `Ditemukan ketidakcocokan data berupa KEKURANGAN kas operasional sebesar ${currencyFormat.format(Math.abs(metrics.cashDifference))}. Direkomendasikan untuk meninjau kembali riwayat transaksi harian dan catatan pengeluaran kasir.`;
  } else {
    assessment = `Ditemukan kelebihan kas operasional sebesar ${currencyFormat.format(metrics.cashDifference)}. Hal ini menandakan adanya pendapatan yang masuk ke kas namun belum terinput secara resmi ke dalam sistem penjualan.`;
  }

  doc.text(assessment, 14, currentY, { maxWidth: pageWidth - 28 });
  currentY += 12;

  // --- Closing Block (Moved to Page 1) ---
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  currentY += 10;
  doc.text(T.issuer_label, 14, currentY);
  currentY += 5;
  doc.setFont('helvetica', 'bold');
  doc.text(T.issuer_name, 14, currentY);

  currentY += 15;
  doc.setDrawColor(200);
  doc.line(14, currentY, 80, currentY);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text(T.signature_note, 14, currentY + 4);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(T.signer_title, 14, currentY - 2);
  
  // Paksa pindah ke halaman kedua untuk lampiran detail
  doc.addPage();
  currentY = 20;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100);
  doc.text("LAMPIRAN DETAIL ANALISIS", 14, currentY);
  doc.setDrawColor(200);
  doc.line(14, currentY + 2, 85, currentY + 2);
  currentY += 15;

  // --- Status Profit/Loss Highlight ---
  const isProfit = metrics.netProfit >= 0;
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.setFont('helvetica', 'bold');
  doc.text("STATUS KINERJA KEUANGAN:", 14, currentY);
  currentY += 5;

  // Tentukan warna teks berdasarkan status profit/loss
  if (isProfit) {
    doc.setTextColor(21, 128, 61);   // Hijau Tua
  } else {
    doc.setTextColor(185, 28, 28);   // Merah Tua
  }
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const statusText = isProfit 
    ? `MENGUNTUNGKAN (PROFIT): Bisnis menghasilkan laba bersih sebesar ${currencyFormat.format(metrics.netProfit)}`
    : `MENGALAMI KERUGIAN (LOSS): Bisnis mengalami kerugian sebesar ${currencyFormat.format(metrics.netProfit)}`;
  doc.text(statusText, 14, currentY + 3);
  
  doc.setTextColor(0); // Reset ke hitam
  doc.setFontSize(10);
  currentY += 12;

  // --- Anomalies Section ---
  if (anomalies && anomalies.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(185, 28, 28);
    doc.text("CATATAN ANOMALI & PERINGATAN SISTEM:", 14, currentY);
    currentY += 5;
    doc.setTextColor(0);

    autoTable(doc, {
      startY: currentY,
      head: [['TEMUAN', 'DESKRIPSI', 'LEVEL']],
      body: anomalies.map(a => [a.title, a.desc, a.type === 'danger' ? 'KRITIKAL' : 'PERINGATAN']),
      theme: 'striped',
      headStyles: { fillColor: [153, 27, 27], textColor: 255 },
      styles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });
    currentY = doc.lastAutoTable.finalY + 15;
  } else {
    currentY += 5;
  }

  if (currentY + 50 > doc.internal.pageSize.height) {
    doc.addPage();
    currentY = 20;
  }

  doc.save(`${filename}.pdf`);
};